import React, { useState, useEffect, useRef } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const DocumentsForm = ({
  selectedStudent,
  setProfilePicSrc,
  refreshProfilePicture,
}) => {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const userId = selectedStudent.userId;

  const [attachments, setAttachments] = useState([]);
  const [profilePictureState, setProfilePictureState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const profilePictureInputRef = useRef(null);

  const fetchStudentDocuments = async () => {
    if (!token || !selectedStudent.studentFirstRegistrationId) {
      console.error("Unauthorized or no student ID.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await get(
        `InspStudentFirstRegistrtion/GetStudentPersonalProfileInfo/${selectedStudent.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess && response.studentDocuments) {
        // Filter out the profile picture from the documents list
        const nonProfileDocuments = response.studentDocuments
          .filter((doc) => doc.documentType !== "pupilPhoto")
          .map((doc) => ({
            name: doc.fileName,
            type: doc.fileName.split(".").pop().toLowerCase(),
            size: 0, // Size not provided in API response, set to 0
            base64: "", // Base64 not provided in API response
            filePath: doc.filePath,
            createdOn: doc.createdOn,
            id: doc.id,
            documentType: doc.documentType,
            tagPriority: doc.tagPriority,
            tagName: doc.tagName,
            tagComments: doc.tagComments,
            documentTagId: doc.documentTagId,
          }));
        setAttachments(nonProfileDocuments);

        // Handle profile picture separately
        const profileDoc = response.studentDocuments.find(
          (doc) => doc.documentType === "pupilPhoto"
        );
        if (profileDoc) {
          const pupilPhoto = {
            name: profileDoc.fileName,
            type: profileDoc.fileName.split(".").pop().toLowerCase(),
            size: 0,
            base64: "",
            filePath: profileDoc.filePath,
            documentType: "pupilPhoto",
            id: profileDoc.id,
            tagPriority: profileDoc.tagPriority,
            tagName: profileDoc.tagName,
            tagComments: profileDoc.tagComments,
            documentTagId: profileDoc.documentTagId,
          };
          setProfilePictureState(pupilPhoto);
          // Update parent component with the fetched profile picture
          setProfilePicSrc(
            profileDoc.filePath
              ? `${process.env.REACT_APP_LIVE_DOMAIN_URL.replace(
                  /\/+$/,
                  ""
                )}/ExternalImages${profileDoc.filePath}`
              : ""
          );
        } else {
          // If no pupilPhoto is found, reset profile picture
          setProfilePictureState(null);
          setProfilePicSrc("");
        }
      }
    } catch (error) {
      console.error("Error fetching student documents:", error);
      toast.error("Failed to fetch documents.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentDocuments();
  }, [selectedStudent.studentFirstRegistrationId]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      const filePromises = files.map((file) =>
        readFileAsBase64(file).then((base64) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          base64,
          documentType: "document",
          fileName: file.name,
          base64String: base64,
        }))
      );
      const newFiles = await Promise.all(filePromises);
      setAttachments((prev) => [...prev, ...newFiles]);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (err) {
      toast.error("Error reading files.");
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validImageTypes.includes(file.type)) {
      toast.error("Please upload only JPG or PNG files for profile picture.");
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Profile picture size should be less than 2MB.");
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      const newProfilePicture = {
        name: file.name,
        type: file.type,
        size: file.size,
        base64,
        documentType: "pupilPhoto",
        fileName: file.name,
        base64String: base64,
      };
      setProfilePictureState(newProfilePicture);
      setProfilePicSrc(`data:${file.type};base64,${base64}`);
      if (profilePictureInputRef.current) {
        profilePictureInputRef.current.value = null;
      }
    } catch (err) {
      toast.error("Error reading profile picture.");
    }
  };

  const handleRemoveFile = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveProfilePicture = () => {
    setProfilePictureState(null);
    setProfilePicSrc("");
    if (profilePictureInputRef.current) {
      profilePictureInputRef.current.value = null;
    }
  };

  const handleViewFile = (file) => {
    if (file.base64 || file.base64String) {
      const base64 = file.base64 || file.base64String;
      const fileType =
        file.type || `image/${file.name.split(".").pop().toLowerCase()}`;
      const dataUrl = `data:${fileType};base64,${base64}`;
      window.open(dataUrl, "_blank");
    } else if (file.filePath) {
      const url = `${process.env.REACT_APP_LIVE_DOMAIN_URL.replace(
        /\/+$/,
        ""
      )}/ExternalImages${file.filePath}`;
      window.open(url, "_blank");
    } else {
      toast.error("Unable to view file: No valid data available.");
    }
  };

  const handleSave = async () => {
    if (attachments.length === 0 && !profilePictureState) {
      toast.error("Please upload at least one document or profile picture.");
      return;
    }

    setIsLoading(true);
    try {
      const documentsPayload = attachments.map((file, index) => ({
        formId: 0,
        studentFirstRegistrationId:
          selectedStudent.studentFirstRegistrationId || 0,
        notificationEventTypeIds: "",
        workflow: {
          createdBy: parseInt(userId) || 0,
          workflowProcessId: 0,
          workflowStepId: 0,
          dbObjectId: 0,
          dbObjectLocationId: 0,
          comments: "",
        },
        id: file.id || 0,
        isActive: true,
        isDeleted: false,
        createdDate: new Date().toISOString(),
        createdById: parseInt(userId) || 0,
        updatedDate: new Date().toISOString(),
        updatedById: parseInt(userId) || 0,
        createdOn: new Date().toISOString(),
        createdBy: userId?.toString() || "",
        updatedOn: new Date().toISOString(),
        updatedBy: userId?.toString() || "",
        fileName: file.name,
        documentType: file.documentType || "document",
        filePath: file.filePath || "",
        base64String: file.base64 || file.base64String || "",
        tagPriority: file.tagPriority || 0,
        tagName: file.tagName || "",
        tagComments: file.tagComments || "",
        documentTagId: file.documentTagId || 0,
      }));

      const profilePicturePayload = profilePictureState
        ? [
            {
              formId: 0,
              studentFirstRegistrationId:
                selectedStudent.studentFirstRegistrationId || 0,
              notificationEventTypeIds: "",
              workflow: {
                createdBy: parseInt(userId) || 0,
                workflowProcessId: 0,
                workflowStepId: 0,
                dbObjectId: 0,
                dbObjectLocationId: 0,
                comments: "",
              },
              id: profilePictureState.id || 0,
              isActive: true,
              isDeleted: false,
              createdDate: new Date().toISOString(),
              createdById: parseInt(userId) || 0,
              updatedDate: new Date().toISOString(),
              updatedById: parseInt(userId) || 0,
              createdOn: new Date().toISOString(),
              createdBy: userId?.toString() || "",
              updatedOn: new Date().toISOString(),
              updatedBy: userId?.toString() || "",
              fileName: profilePictureState.name,
              documentType: "pupilPhoto",
              filePath: profilePictureState.filePath || "",
              base64String:
                profilePictureState.base64 ||
                profilePictureState.base64String ||
                "",
              tagPriority: profilePictureState.tagPriority || 0,
              tagName: profilePictureState.tagName || "Profile Picture",
              tagComments: profilePictureState.tagComments || "",
              documentTagId: profilePictureState.documentTagId || 0,
            },
          ]
        : [];

      const requestBody = {
        formId: 0,
        studentFirstRegistrationId:
          selectedStudent.studentFirstRegistrationId || 0,
        notificationEventTypeIds: "",
        workflow: {
          createdBy: parseInt(userId) || 0,
          workflowProcessId: 0,
          workflowStepId: 0,
          dbObjectId: 0,
          dbObjectLocationId: 0,
          comments: "",
        },
        studentDocuments: [...documentsPayload, ...profilePicturePayload],
      };

      await post(
        "InspStudentFirstRegistrtion/UpdateStudentStudentDocumentInfo",
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Documents and profile picture saved successfully.");

      await fetchStudentDocuments();

      refreshProfilePicture();

      // setProfilePictureState(null);
    } catch (err) {
      console.error("Error saving documents:", err);
      toast.error(
        "An error occurred while saving documents and profile picture."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-content-area d-flex flex-column h-100">
      <div className="mb-5">
        <h5 className="mb-0 fw-bold">Document Attachments</h5>
      </div>
      <div className="profile-scrollable-area p-1 flex-grow-1">
        <h5 className="mb-3 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          UPLOAD DOCUMENT
        </h5>
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h6 className="fw-semibold text-muted mb-3">Profile Picture</h6>
            <div className="mb-3">
              <div className="input-group">
                <input
                  type="file"
                  className="form-control custom-input"
                  accept=".jpg,.jpeg,.png"
                  ref={profilePictureInputRef}
                  onChange={handleProfilePictureChange}
                />
                {/* <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => profilePictureInputRef.current?.click()}
                >
                  <i className="fas fa-upload me-1"></i>
                  Browse
                </button> */}
              </div>
              <small className="form-text text-muted mt-1">
                Accepted formats: JPG, PNG (Max size: 2MB)
              </small>
            </div>
            {profilePictureState && (
              <div className="mt-3">
                <h6 className="fw-semibold text-muted mb-2">
                  Current Profile Picture
                </h6>
                <div
                  className="d-flex align-items-center border rounded p-3"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  <i className="fas fa-user-circle me-3 text-primary fs-4"></i>
                  <div className="flex-grow-1">
                    <div
                      className="text-truncate"
                      style={{ maxWidth: "300px" }}
                    >
                      {profilePictureState.name}
                    </div>
                    <small className="text-muted">
                      {profilePictureState.size > 0
                        ? `Size: ${(
                            profilePictureState.size /
                            1024 /
                            1024
                          ).toFixed(2)} MB`
                        : "Size not available"}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleViewFile(profilePictureState)}
                      title="View profile picture"
                    >
                      <i className="fas fa-eye"></i> View
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleRemoveProfilePicture}
                      title="Remove profile picture"
                    >
                      <i className="fas fa-trash"></i> Remove
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="card shadow-sm">
          <div className="card-body">
            <h6 className="fw-semibold text-muted mb-3">
              Document Attachments
            </h6>
            <div className="mb-3">
              <p className="text-muted mb-3">
                Upload documents such as Birth Certificate, B-Form, and CNIC.
              </p>
            </div>
            <div className="mb-3">
              <div className="input-group">
                <input
                  type="file"
                  className="form-control custom-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                {/* <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="fas fa-upload me-1"></i>
                  Browse
                </button> */}
              </div>
              <small className="form-text text-muted mt-1">
                Accepted formats: PDF, JPG, PNG (Max size: 5MB)
              </small>
            </div>
            {attachments.length > 0 && (
              <div className="mt-4">
                <h6 className="fw-semibold text-muted mb-3">
                  Uploaded Documents
                </h6>
                <div className="list-group">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="list-group-item d-flex justify-content-between align-items-center border rounded mb-2 p-3"
                      style={{ backgroundColor: "#f8f9fa" }}
                    >
                      <div className="d-flex align-items-center">
                        <i className="fas fa-file-alt me-3 text-primary fs-5"></i>
                        <div>
                          <div
                            className="text-truncate"
                            style={{ maxWidth: "300px" }}
                          >
                            {file.name}
                          </div>
                          <small className="text-muted">
                            {file.size > 0
                              ? `Size: ${(file.size / 1024 / 1024).toFixed(
                                  2
                                )} MB`
                              : "Size not available"}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleViewFile(file)}
                          title="View file"
                        >
                          <i className="fas fa-eye"></i> View
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveFile(index)}
                          title="Remove file"
                        >
                          <i className="fas fa-trash"></i> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-auto pt-3">
        <div className="d-flex justify-content-end gap-3">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentsForm;
